import { splitParentSummary } from './pursuit/splitParentSummary.js'
/* =========================================================================
 * THE FACE OF A WORK ROW - what it is, who won it, who we sell to, and why
 * it is on the list. One pure function, read by BOTH work surfaces.
 *
 * Brian Kass, 2026-10-26: "command center - working queue does not show
 * champion. it just shows an account. its useless" and, the same day,
 * "work list doesn't include who the champion is either."
 *
 * Measured on production the same afternoon (frontend 2.132.6 @ fb5af85,
 * backend 9d26cc7), at a real 390px viewport:
 *
 *   - Command Center Work queue: a row was the record name, a next-action
 *     sentence, and "rep - date - confidence" with the champion as an
 *     unlabelled trailing token (only when one existed). No champion label,
 *     no athletes, no account, no Art/Quote/Email/Win - only "Open record".
 *   - My Worklist: the champion line existed, but revenue ordering flattened
 *     the crown tier into the dollar sort, so for Zach Muller (104 unworked
 *     October crowns) and Chris Powell (35) the 30 rows drawn held ZERO
 *     champions. Philip Davanzo III's held 5. The crown pin that fixes the
 *     ordering is on feat/last-hunt-20261026 and this change stacks on it.
 *   - The payload: `sr_winning_school` (the ACCOUNT behind an individual or
 *     pro champion - "Mollie Schlichter" is the champion, Oakland Catholic is
 *     who buys the ring) is fetched by CORE_PROPS and dropped by the
 *     snapshot's slim(); `governing_body` reaches the board on 15,050 records
 *     and the normalizer read `sr_governing_body`, which does not exist, so
 *     the body was blank on every row.
 *
 * So the face answers four questions, in the order a rep reads them:
 *   1. WHAT   the championship - body, sport, class, state, date.
 *   2. WHO    the champion - a TEAM row (the school), or ONE indy-ring row
 *             listing every athlete with their school. Never a bare blank:
 *             no champion yet, withdrawn and multi-school each say so.
 *   3. SELL   the account - the school or organisation that buys the ring.
 *   4. WHY    the reason this row is on this list, in words.
 *
 * PURE. No React, no fetch. `championFace.test.js` drives it. Return shapes
 * use `type:` (never `kind:`) so check-action-kinds.mjs does not read a
 * champion state as an action.
 * ========================================================================= */
import { athletesOn, sameSchool } from './pursuit/splitFamily.js'
import { titleTypeOf, isMultiSchool, schoolGroups } from '../../lib/championDisplay.js'
import { pricingDeepLink } from '../../lib/championToClose.js'
import { titleTierFor, TITLE_TIER_LABELS } from '../../lib/titleTierView.js'

const S = (v) => (v == null ? '' : String(v)).trim()

/* The record's class / division, read off what the record carries. The object
 * has no class property the board receives, so the NAME is the source: "Golf
 * Boys 3A", "Division II", "Class AA", "1A-Division I". Nothing is inferred
 * beyond what the name states. */
const CLASS_RE = [
  /\b(\d{1,2}A(?:-Division\s+[IVX]+|-?[IVX]+)?)\b/,
  /\b(Division\s+(?:[IVX]+|\d+)(?:-[A-Z]+)?)\b/i,
  /\b(Class\s+[A-Z0-9]{1,4})\b/i,
  /\b(D[1-3])\b/,
]
export function classOf(c) {
  const explicit = S(c?.division)
  if (explicit) return explicit
  const name = S(c?.name ?? c?.championship_name)
  for (const re of CLASS_RE) { const m = name.match(re); if (m) return m[1] }
  return ''
}

/** WHAT: the championship, one labelled part each so nothing reads as a blob. */
export function championshipFace(c) {
  return {
    name: S(c?.name ?? c?.championship_name),
    body: S(c?.governingBody ?? c?.governing_body),
    sport: S(c?.sport ?? c?.sr_sport),
    classLabel: classOf(c),
    level: S(c?.level ?? c?.sr_level),
    state: S(c?.state ?? c?.sr_state),
    date: c?.effDate || null,
  }
}

/**
 * WHO WON, and WHO WE SELL TO.
 *
 * type
 *   'team'        one school/team. `team` is the champion; `account` is the
 *                 school behind it (sr_winning_school when it differs - "Texas
 *                 Longhorns" won, "University of Texas" is the account).
 *   'individual'  ONE indy-ring row. `athletes` lists every winner with their
 *                 school; `account` is the school(s) the ring is sold to.
 *   'multi'       won by several schools, no single champion named (Brian
 *                 2026-10-19). Schools listed with their athletes.
 *   'withdrawn'   a crown that was cleared; the retraction text is the reason.
 *   'none'        nothing recorded yet - and the note says what happens next.
 *
 * ⚠️ A record that NAMES a champion is crowned, whatever provenance sits in
 * `champion_detected_raw` - the same rule the two fix branches landed today.
 */
export function championFace(c) {
  const face = winnerFace(c)
  const routing = c?.isIndyGroup
    ? { titleTier: 'review_required', titleTierReason: 'Select an actual member record to review its title, account and access gates.' }
    : titleTierFor(c)
  const ring = routing.titleTier === 'ring_tier'
  return { ...face, titleTier: routing.titleTier, policyLabel: TITLE_TIER_LABELS[routing.titleTier],
    headline: ring ? face.headline : face.headline.replace(/Indy ring/g, 'Individual grouping').replace(/champions/g, 'winners').replace(/champion/g, 'winner').replace(/Champion/g, 'Winner'),
    policyReason: !ring ? routing.titleTierReason : '' }
}

function winnerFace(c) {
  const family = splitParentSummary(c)
  if (family) return { type: 'split', headline: family.headline, team: '', athletes: [], account: '',
    accountNote: 'Review the matched child records for their individual accounts.', note: [family.note, ...family.reasons].join(' ') }

  const champion = S(c?.champion ?? c?.current_champion)
  const winningSchool = S(c?.winningSchool ?? c?.sr_winning_school)
  const named = champion || winningSchool
  const retraction = S(c?.championDetectedRaw ?? c?.champion_detected_raw)
  const year = S(c?.seasonYear ?? c?.sr_season_year)

  /* A synthesized indy-ring item (splitFamily.collapseForWorklist) carries its
   * athletes in `references` and no champion of its own. */
  if (c?.isIndyGroup) {
    const athletes = (c.references || []).map((a) => ({
      athlete: S(a.athlete), school: S(a.school), event: S(a.event) }))
    return individual(athletes, year)
  }

  if (!named) {
    if (isMultiSchool(c)) return multi(c)
    if (retraction) {
      return { type: 'withdrawn', headline: 'Champion withdrawn', team: '', athletes: [],
        account: '', accountNote: 'No account while the crown is withdrawn.', note: retraction }
    }
    return { type: 'none', headline: 'No champion recorded yet', team: '', athletes: [],
      account: '',
      accountNote: 'The account is the champion, so it is known once the champion is.',
      note: S(c?.championSearchTag) === 'requested'
        ? 'A hunt is already requested on this record.'
        : 'Find and verify the champion to name the account.' }
  }

  if (isMultiSchool(c)) return multi(c)

  const type = titleTypeOf(c)
  if (type === 'individual') {
    let athletes = athletesOn(c).map((a) => ({ athlete: S(a.athlete), school: S(a.school), event: S(a.event) }))
    /* The legacy shape: the ATHLETE stored as the champion and the school in
     * sr_winning_school ("Mollie Schlichter" / Oakland Catholic). athletesOn
     * reads the champion as a school there; correct it from the record. */
    if (winningSchool && champion && !sameSchool(champion, winningSchool)
      && athletes.length === 1 && !athletes[0].athlete && sameSchool(athletes[0].school, champion)) {
      athletes = [{ athlete: champion, school: winningSchool, event: athletes[0].event }]
    }
    return individual(athletes, year, winningSchool)
  }

  /* TEAM. The champion is the team; the account is the school behind it. */
  const team = champion || winningSchool
  /* sr_winning_school is the school field by definition, so where it is set it
   * IS the account ("Texas Longhorns" won; "University of Texas" buys). */
  const account = winningSchool || team
  return { type: 'team', headline: 'Team champion', team, year, athletes: [], account,
    accountNote: '', note: '' }
}

function individual(athletes, year, winningSchool = '') {
  const schools = [...new Set(athletes.map((a) => a.school).filter(Boolean))]
  const account = schools.length ? schools.join(' · ') : winningSchool
  return {
    type: 'individual',
    headline: athletes.length > 1 ? `Indy ring · ${athletes.length} champions` : 'Individual champion',
    team: '', year, athletes, account,
    accountNote: account ? '' : 'No school stated for this athlete - the ring has no account until one is.',
    note: athletes.some((a) => !a.athlete) ? 'An athlete is not named yet - the school is.' : '',
  }
}

function multi(c) {
  const g = schoolGroups(c)
  const groups = g.ok ? g.groups : []
  return {
    type: 'multi', headline: 'Won by several schools - no single champion named', team: '',
    athletes: groups.flatMap((x) => x.athletes.map((a) => ({
      athlete: S(a.athlete), school: S(a.school) || S(x.school), event: S(a.event) }))),
    account: groups.map((x) => x.school).filter(Boolean).join(' · '),
    accountNote: 'Each school is its own account.',
    note: 'Resolve which school or schools to pursue.',
  }
}

/**
 * The pricing link for Quote, shared by the Command Center console, the Work
 * queue row and My Worklist so the three cannot pre-fill different schools.
 * One account per quote: an indy ring spanning several schools does not pre-fill
 * a joined list - the rep picks the school in pricing.
 */
export function quoteUrlFor(c) {
  if (c?.isIndyGroup || splitParentSummary(c) || titleTierFor(c).titleTier !== 'ring_tier') return null
  const f = championFace(c)
  const single = f.account && !f.account.includes(' · ') ? f.account : ''
  /* Never the raw champion slot on an indy ring: it holds the grouping text
   * ("3 individual champions · 2 schools"), not a school. */
  return pricingDeepLink({
    championshipId: c?.id,
    champion: { school: single || (f.type === 'team' ? f.team : '') },
    championship: { sport: c?.sport, level: c?.level },
  })
}

/** One plain line for the CSV / a screen reader / a test. Never empty. */
export function championLine(face) {
  if (face.type === 'team') return `${face.team}${face.year ? ` ${face.year}` : ''}`
  if (face.type === 'individual' || face.type === 'multi') {
    const list = face.athletes.map((a) => [a.athlete || 'Athlete not named yet', a.school && `(${a.school})`]
      .filter(Boolean).join(' ')).join('; ')
    return list || face.headline
  }
  return face.headline
}
